import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, PenLine, CheckCircle, Clock, Plus, ChevronRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';

interface Sermon {
  id: string;
  title: string;
  status: string;
  updated_at: string;
}

interface StatusCounts {
  total: number;
  draft: number;
  complete: number;
}

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  const month = date.toLocaleString('default', { month: 'short' });
  return `${month} ${date.getDate()}`;
}

export function SermonStats() {
  const { user } = useAuth();
  const { isCollaborator } = useSubscription();
  const navigate = useNavigate();
  const [counts, setCounts] = useState<StatusCounts>({ total: 0, draft: 0, complete: 0 });
  const [recentSermons, setRecentSermons] = useState<Sermon[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    async function fetchStats() {
      setLoading(true);
      try {
        if (isCollaborator) {
          // Collaborators see sermons shared with them
          const { data: shared } = await supabase
            .from('team_sermon_shares')
            .select('sermon_id, sermons(id, title, status, updated_at)')
            .limit(5);

          const sermons = (shared ?? [])
            .map((row: any) => row.sermons)
            .filter(Boolean) as Sermon[];

          setCounts({ total: sermons.length, draft: 0, complete: 0 });
          setRecentSermons(sermons.slice(0, 5));
        } else {
          const [statusRes, recentRes] = await Promise.all([
            supabase
              .from('sermons')
              .select('status')
              .eq('user_id', user.id)
              .eq('archived', false),
            supabase
              .from('sermons')
              .select('id, title, status, updated_at')
              .eq('user_id', user.id)
              .eq('archived', false)
              .order('updated_at', { ascending: false })
              .limit(5),
          ]);

          const statusData = statusRes.data ?? [];
          const derived: StatusCounts = { total: 0, draft: 0, complete: 0 };
          statusData.forEach((s: { status: string }) => {
            derived.total++;
            if (s.status === 'draft') derived.draft++;
            if (s.status === 'complete') derived.complete++;
          });
          setCounts(derived);
          setRecentSermons(recentRes.data ?? []);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, [user, isCollaborator]);

  const statCards = isCollaborator
    ? [{ label: 'Shared With Me', value: counts.total, icon: <FileText className="h-8 w-8 text-electric/50" /> }]
    : [
        { label: 'Total Sermons', value: counts.total, icon: <FileText className="h-8 w-8 text-electric/50" /> },
        { label: 'Drafts', value: counts.draft, icon: <PenLine className="h-8 w-8 text-gold/50" /> },
        { label: 'Complete', value: counts.complete, icon: <CheckCircle className="h-8 w-8 text-emerald/50" /> },
      ];

  return (
    <div className="space-y-4">
      {/* Stat cards */}
      <div className={`grid gap-4 ${isCollaborator ? 'grid-cols-1 sm:grid-cols-1' : 'grid-cols-1 sm:grid-cols-3'}`}>
        {loading
          ? statCards.map((_, i) => (
              <div key={i} className="bg-white dark:bg-charcoal rounded-lg shadow-sm border border-sand/20 dark:border-slate/20 p-4 animate-pulse">
                <div className="h-4 bg-sand/40 rounded w-24 mb-3" />
                <div className="h-8 bg-sand/40 rounded w-12" />
              </div>
            ))
          : statCards.map((card) => (
              <div key={card.label} className="bg-white dark:bg-charcoal rounded-lg shadow-sm border border-sand/20 dark:border-slate/20 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate dark:text-soft-white/60">{card.label}</p>
                    <p className="text-2xl font-bold text-midnight dark:text-soft-white mt-0.5">{card.value}</p>
                  </div>
                  {card.icon}
                </div>
              </div>
            ))}
      </div>

      {/* Recent sermons */}
      <div className="bg-white dark:bg-charcoal rounded-lg shadow-sm border border-sand/20 dark:border-slate/20">
        <div className="flex items-center justify-between px-4 py-3 border-b border-sand/20 dark:border-slate/20">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate dark:text-soft-white/60" />
            <h2 className="text-sm font-semibold text-midnight dark:text-soft-white">
              {isCollaborator ? 'Shared Sermons' : 'Recently Updated'}
            </h2>
          </div>
          {!isCollaborator && (
            <button
              onClick={() => navigate('/dashboard/editor')}
              className="flex items-center gap-1 text-xs font-medium text-electric hover:text-electric/80 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              New Sermon
            </button>
          )}
        </div>

        {loading ? (
          <div className="divide-y divide-sand/20 dark:divide-slate/20">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="px-4 py-3 animate-pulse flex items-center justify-between">
                <div className="space-y-1.5">
                  <div className="h-3.5 bg-sand/40 rounded w-48" />
                  <div className="h-3 bg-sand/40 rounded w-16" />
                </div>
                <div className="h-3 bg-sand/40 rounded w-12" />
              </div>
            ))}
          </div>
        ) : recentSermons.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-slate dark:text-soft-white/50">No sermons yet.</p>
            {!isCollaborator && (
              <button
                onClick={() => navigate('/dashboard/editor')}
                className="mt-3 text-sm text-electric hover:underline"
              >
                Create your first sermon →
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-sand/20 dark:divide-slate/20">
            {recentSermons.map((sermon) => (
              <button
                key={sermon.id}
                onClick={() => navigate(`/dashboard/editor/${sermon.id}`)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-sand/10 dark:hover:bg-slate/10 transition-colors text-left group"
              >
                <div className="min-w-0 flex-1 mr-4">
                  <p className="text-sm font-medium text-midnight dark:text-soft-white truncate">
                    {sermon.title || 'Untitled Sermon'}
                  </p>
                  <span className={`inline-block text-xs mt-0.5 px-1.5 py-0.5 rounded font-medium ${
                    sermon.status === 'complete'
                      ? 'bg-emerald/10 text-emerald dark:bg-emerald/20'
                      : 'bg-gold/10 text-gold dark:bg-gold/20'
                  }`}>
                    {sermon.status === 'complete' ? 'Complete' : 'Draft'}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-xs text-slate dark:text-soft-white/50">
                    {formatRelativeDate(sermon.updated_at)}
                  </span>
                  <ChevronRight className="h-3.5 w-3.5 text-slate/40 dark:text-soft-white/30 group-hover:text-electric transition-colors" />
                </div>
              </button>
            ))}
          </div>
        )}

        {!isCollaborator && counts.total > 5 && (
          <div className="px-4 py-2.5 border-t border-sand/20 dark:border-slate/20">
            <button
              onClick={() => navigate('/dashboard/library')}
              className="text-xs text-electric hover:underline"
            >
              View all {counts.total} sermons →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
